import { AlertTriangle, CheckCircle, Info, XCircle } from 'lucide-react';
import type { ReactNode } from 'react';

interface AlertProps {
  variant: 'danger' | 'warning' | 'info' | 'success';
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
  children?: ReactNode;
}

const alertConfig = {
  danger: {
    icon: XCircle,
    styles: 'bg-red-50 border-red-300 text-red-800',
    iconStyles: 'text-red-500',
  },
  warning: {
    icon: AlertTriangle,
    styles: 'bg-yellow-50 border-yellow-300 text-yellow-800',
    iconStyles: 'text-yellow-500',
  },
  info: {
    icon: Info,
    styles: 'bg-blue-50 border-blue-300 text-blue-800',
    iconStyles: 'text-blue-500',
  },
  success: {
    icon: CheckCircle,
    styles: 'bg-green-50 border-green-300 text-green-800',
    iconStyles: 'text-green-500',
  },
};

/**
 * Alert component for displaying status messages with icon, title, description, and optional actions.
 */
export function Alert({
  variant,
  title,
  description,
  actions,
  className = '',
  children,
}: AlertProps) {
  const config = alertConfig[variant];
  const Icon = config.icon;

  return (
    <div
      className={`rounded-md border p-4 ${config.styles} ${className}`}
      role='alert'
    >
      <div className='flex items-start'>
        <Icon
          className={`mr-3 size-5 shrink-0 ${config.iconStyles}`}
          aria-hidden='true'
        />
        <div className='flex-1'>
          <div className='font-semibold'>{title}</div>
          {description && <div className='mt-1 text-sm'>{description}</div>}
          {children && (
            <div className={`mt-2 text-sm ${description ? 'pt-1' : ''}`}>
              {children}
            </div>
          )}
          {actions && (
            <div className='mt-4 flex justify-end gap-2'>{actions}</div>
          )}
        </div>
      </div>
    </div>
  );
}
